import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../hooks/useApi";
import { format, isToday, formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Heart,
  Activity,
  ArrowRight,
  TrendingUp,
  MessageSquare,
  Calendar,
  Quote,
} from "lucide-react";
import { clsx } from "clsx";
import { PageHeader } from "../components/PageHeader";
import { Link } from "react-router-dom";

export function Dashboard() {
  const apiClient = useApi();
  const [selectedMoodEntry, setSelectedMoodEntry] = useState<any>(null);

  const {
    data: status,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["status"],
    queryFn: () => apiClient.getStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch historical check-ins with mood data
  const { data: checkInsData } = useQuery({
    queryKey: ["checkins", { limit: 14 }], // Last 14 days
    queryFn: () => apiClient.get("/api/v1/checkins?limit=14"),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Failed to load status</h3>
            <div className="mt-2 text-sm text-red-700">{error.message}</div>
            <div className="mt-4">
              <button
                onClick={() => refetch()}
                className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-2 rounded-md text-sm font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!status) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "alive":
        return "text-green-600";
      case "warning":
        return "text-yellow-600";
      case "triggered":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "alive":
        return CheckCircle;
      case "warning":
        return Clock;
      case "triggered":
        return AlertTriangle;
      default:
        return Heart;
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case "alive":
        return "Great! You're still with us";
      case "warning":
        return "Check-in Soon";
      case "triggered":
        return "Action Required";
      default:
        return "System Status";
    }
  };

  const StatusIcon = getStatusIcon(status.status);

  // Check if user has checked in today
  const hasCheckedInToday =
    status && status.lastCheckin ? isToday(new Date(status.lastCheckin)) : false;

  return (
    <div className="space-y-6">
      <PageHeader title="Journey" description="Monitor your mAlive status" icon={Activity} />

      {/* Check-in Prompt */}
      {!hasCheckedInToday && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-yellow-600" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Daily Check-in Required</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  You haven't checked in today. Please confirm you're safe.
                </p>
              </div>
            </div>
            <Link
              to="/checkin"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
            >
              Check In Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Status Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex-shrink-0">
              <StatusIcon className={clsx("h-8 w-8", getStatusColor(status.status))} />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Current Status</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {getStatusMessage(status.status)}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Quote */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <Quote className="h-6 w-6 text-indigo-400 mr-3" />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Daily Inspiration</h3>
              <p className="mt-1 text-sm text-gray-500 italic">
                "Every day is a new beginning. Take a deep breath and start again."
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Combined Check-in Schedule */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <Calendar className="h-6 w-6 text-gray-400 mr-3" />
            <h3 className="text-lg leading-6 font-medium text-gray-900">Check-in Schedule</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Last Check-in */}
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Last Check-in</dt>
              <dd className="text-lg font-medium text-gray-900">
                {status.lastCheckin ? format(new Date(status.lastCheckin), "PPp") : "N/A"}
              </dd>
            </div>

            {/* Next Check-in Due */}
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Next Check-in Due</dt>
              <dd className="text-lg font-medium text-gray-900">
                {format(new Date(status.nextExpectedCheckin), "PPp")}
              </dd>
            </div>

            {/* Time Until Next Check-in */}
            <div className="md:col-span-2">
              <dt className="text-sm font-medium text-gray-500 mb-2">Time Until Next Check-in</dt>
              <dd className="text-base text-gray-900">
                <span className="font-medium">
                  {Math.max(0, Math.floor(status.timeUntilNextCheckin / (1000 * 60 * 60)))} hours{" "}
                  {Math.max(
                    0,
                    Math.floor((status.timeUntilNextCheckin % (1000 * 60 * 60)) / (1000 * 60))
                  )}{" "}
                  minutes
                </span>{" "}
                remaining until your next check-in is due.
              </dd>
            </div>

            {/* Confidence Level */}
            <div className="md:col-span-2">
              <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center">
                Check-in Confidence
                <span
                  className="ml-1 text-xs text-gray-400 cursor-help"
                  title="Confidence level based on your check-in reliability. Higher confidence indicates consistent check-in behavior."
                >
                  ⓘ
                </span>
              </dt>
              <dd className="text-lg font-medium text-gray-900">
                {status.lastCheckin ? `${(status.confidence * 100).toFixed(1)}%` : "N/A"}
              </dd>
            </div>
          </div>

          {status.timeUntilNextCheckin < 0 && (
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Check-in Overdue</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    Your check-in is overdue. Please check in as soon as possible.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Journey */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <TrendingUp className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg leading-6 font-medium text-gray-900">Journey</h3>
          </div>

          {(checkInsData as any)?.data?.checkIns &&
          (checkInsData as any).data.checkIns.length > 0 ? (
            <div className="space-y-3">
              {(checkInsData as any).data.checkIns
                .filter((checkIn: any) => checkIn.moodScore) // Only show entries with mood data
                .slice(0, 7) // Show last 7 mood entries
                .map((checkIn: any) => (
                  <div
                    key={checkIn.id}
                    onClick={() => setSelectedMoodEntry(checkIn)}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div
                          className={clsx(
                            "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium",
                            checkIn.moodScore <= 3
                              ? "bg-red-500"
                              : checkIn.moodScore >= 4 && checkIn.moodScore <= 6
                                ? "bg-yellow-500"
                                : "bg-green-500"
                          )}
                        >
                          {checkIn.moodScore}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Mood Score {checkIn.moodScore}/10
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDistanceToNow(new Date(checkIn.timestamp), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {checkIn.moodLabels.length > 0 && (
                        <span className="text-xs text-gray-500">
                          {checkIn.moodLabels.length} label
                          {checkIn.moodLabels.length !== 1 ? "s" : ""}
                        </span>
                      )}
                      {checkIn.message && <MessageSquare className="h-4 w-4 text-gray-400" />}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Heart className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No mood data yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Start tracking your mood by adding mood data to your check-ins.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mood Details Modal */}
      {selectedMoodEntry && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={() => setSelectedMoodEntry(null)}
        >
          <div
            className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Mood Details</h3>
              <button
                onClick={() => setSelectedMoodEntry(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <div
                    className={clsx(
                      "w-10 h-10 rounded-full flex items-center justify-center text-white font-medium",
                      selectedMoodEntry.moodScore <= 3
                        ? "bg-red-500"
                        : selectedMoodEntry.moodScore >= 4 && selectedMoodEntry.moodScore <= 6
                          ? "bg-yellow-500"
                          : "bg-green-500"
                    )}
                  >
                    {selectedMoodEntry.moodScore}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      Mood Score {selectedMoodEntry.moodScore}/10
                    </p>
                    <p className="text-sm text-gray-500">
                      {format(new Date(selectedMoodEntry.timestamp), "PPPp")}
                    </p>
                  </div>
                </div>
              </div>

              {selectedMoodEntry.moodLabels && selectedMoodEntry.moodLabels.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Mood Labels:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMoodEntry.moodLabels.map((label: string) => (
                      <span
                        key={label}
                        className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedMoodEntry.message && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Message:</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                    {selectedMoodEntry.message}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
